import { useCallback } from 'react';
import { IntercompanyReturnLink } from 'returnCenter-api';
import useAppDispatch from 'hooks/useAppDispatch';
import useAppSelector from 'hooks/useAppSelector';
import { companiesSelector, selectCompanySet } from 'store/reducers/companiesSlice';

/**
 * Opens a return linked across companies. The other end of an intercompany link lives in another
 * company, so opening it means switching the company selector first — and reloading, so everything
 * already loaded for this company is dropped.
 *
 * A link into a company the user cannot select is not openable: after the reload the selector would
 * fall back to the first company and the return would not be found there.
 */
const useOpenLinkedReturn = () => {
    const dispatch = useAppDispatch();
    const companies = useAppSelector(companiesSelector.selectCompanies);

    const canOpen = useCallback(
        (link: IntercompanyReturnLink) =>
            !!link.returnId &&
            (!link.contextId ||
                (companies ?? []).some(
                    (c) => c.id === link.contextId && (c.marketId ?? undefined) === (link.marketId ?? undefined),
                )),
        [companies],
    );

    const open = useCallback(
        (link: IntercompanyReturnLink) => {
            if (!canOpen(link)) return;
            if (link.contextId) {
                dispatch(selectCompanySet({ contextId: link.contextId, marketId: link.marketId ?? undefined }));
            }
            window.location.href = `/returns/all/${link.returnId}`;
        },
        [dispatch, canOpen],
    );

    return { open, canOpen };
};

export default useOpenLinkedReturn;
